import styled from "styled-components";
import { useState, useContext, useCallback, useMemo } from "react";
import { useQueryClient, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import AlertDisplay from "../common/AlertDisplay";
import { useGameQuery } from "../settings/Settings.state";
import {
  pageState,
  pageSizeState,
  challengeIdState,
  monitorQueueState,
  teamIdState,
  useMonitorRefresh,
  useStartGameMutation,
  useStopGameMutation,
  useAnswersDataQuery,
  monitorFilterTypeState,
} from "./Monitor.state";
import { useUserQuery } from "./User.state";
import { Button, Row, Col } from "../App.styles";
import {
  Alert,
  Loadable,
  Pagination,
  Refresher,
  SidebarLayout,
  Spinner,
} from "../common";
import { usePageListeners } from "../common/Refresher.state";
import { FormAutocomplete } from "../common/FormAutocomplete";
import ROUTE_PATTERNS from "../consts/routes";
import MediaOverlay from "./MediaOverlay";
import { axios } from "../lib/request";
import { leaderboardQueryKey } from "../lib/refresh";
import Answer from "./Answer";
import LeaderboardWidget from "./LeaderboardWidget";
import MonitorPlaceholder, { AnswerPlaceholder } from "./MonitorPlaceholder";
import { withPlaceholder, ConfirmationModal } from "../common";
import React from "react";
import MonitorEmpty from './MonitorEmpty'
import { useDecision } from '@optimizely/react-sdk'
import { useDebouncedCallback } from 'use-debounce'
import { useTeamsOptionsInfiniteQuery } from '../teams/Teams.state'
import { OrganizeContext, OrganizeContextProvider } from '../organize/Organize.context';
import { Blast } from '../teams/sidebar-cards'
import { alertState } from "../common/AlertDisplay/AlertDisplay.state";

const HeaderRow = styled(Row)`
  ${({ theme }) => `
    margin-bottom: 1rem;

    .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.Mui-focused, 
    .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-outlined.MuiFormLabel-filled {
      background-color: ${theme.lightColor};
    }
  `}
`;

const DeleteButton = styled(Button)`
  min-width: 0;
`;

const GameButton = styled(Button)`
  min-width: 0;
`;

const AlertWrapper = styled.span`
  ${Button} {
    margin-right: 1rem;
  }
`;

const ChallengeSelect = ({ setValue, value }) => {
    const { t } = useTranslation();
    const { challenges: sourceChallenges } = useContext(OrganizeContext)
    const { isLoading, tasks: challenges = [] } = sourceChallenges || {}
  
    return (
        <FormAutocomplete
          loading={isLoading}
          options={challenges}
          disableClearable={true}
          size="small"
          defaultText={t("All Challenges")}
          value={value}
          onChange={(e, v) => setValue(v?.id)}
          stateful={false}
          label={t("Challenges")}
        />
    )
  };

const TeamSelect = ({ setValue, value }) => {
  const { t } = useTranslation();
  const [term, setTerm] = useState('')
  const onRemoteSearch = useDebouncedCallback((query) => {
    setTerm(query)
  }, 600)

  const { data, fetchNextPage, isFetching, hasNextPage } = useTeamsOptionsInfiniteQuery({ term })

  const options = useMemo(() => {
    return (data?.pages || []).map(p => p?.teams || []).flat()
  }, [data])
  
  const onNextPage = useCallback(async () => {
    if (isFetching || !hasNextPage) {
      return
    }
    await fetchNextPage()
  }, [isFetching, hasNextPage, fetchNextPage])

  return (
    <FormAutocomplete
      loading={isFetching}
      options={options}
      disableClearable={true}
      size="small"
      defaultText={term ? undefined : t("All Teams")}
      value={value}
      onChange={(e, v) => setValue(v?.id)}
      onRemoteSearch={onRemoteSearch}
      onNextPage={onNextPage}
      stateful={false}
      label={t("Teams")}
    />
  )
};

const TypeSelect = ({ setValue, value }) => {
  const { t } = useTranslation();

  return (
    <FormAutocomplete
        options={[
            { id: 1, name: t("Photo") },
            { id: 2, name: t("Video") },
            { id: 5, name: t("Trivia") },
            { id: 6, name: t("Multiple Choice") },
            { id: 3, name: t("Sticker + Photo") },
            { id: 0, name: t("Text") },
            { id: 4, name: t("QR Code") },
            { id: 9, name: t("Background + Photo") },
            { id: 10, name: t("Background + Video") },
            { id: 11, name: t("Split Path") },
        ]}
        disableClearable={true}
        size="small"
        defaultText={t("All Types")}
        value={value}
        onChange={(e, v) => setValue(v?.id)}
        stateful={false}
        label={t("Types")}
    />
  )
};

const Monitor = () => {
  const { t } = useTranslation();
  const { game_id } = useParams();
  const { data = {}, isFetching, isLoading } = useAnswersDataQuery(game_id);
  const { answers = [], meta = {} } = data
  const { pages = [] } = meta
  const [overlayId, setOverlayId] = useState();
  const [page, setPage] = useRecoilState(pageState);
  const [pageSize, setPageSizeState] = useRecoilState(pageSizeState);
  const [challengeId, setChallengeId] = useRecoilState(challengeIdState);
  const [teamId, setTeamId] = useRecoilState(teamIdState);
  const [monitorFilterType, setMonitorFilterType] = useRecoilState(monitorFilterTypeState);
  const [game] = useGameQuery(game_id);
  const { game: gameRole = {} } = useUserQuery(game_id);
  const { administrator, coordinator, super_admin } = gameRole;
  const queryClient = useQueryClient();
  const { startGame, isStarting } = useStartGameMutation();
  const { stopGame, isStopping } = useStopGameMutation();
  const monitorQueue = useRecoilValue(monitorQueueState(game_id));
  const refresh = useMonitorRefresh(game_id);
  const setAlert = useSetRecoilState(alertState)
  const [decisionFilterType] = useDecision('monitor-filter-type')
  const answerIds = useMemo(() => answers.map(answer => answer.id), [answers])
  const listeners = usePageListeners(ROUTE_PATTERNS.ANSWERS_MONITOR, { answerIds });

  const setPageSize = pageSize => {
    setPageSizeState(Number(pageSize))
  }

  const { isLoading: isDeletingAll, mutateAsync: deleteAll } = useMutation(
    () =>
      axios.delete(
        `${process.env.REACT_APP_API_EMPTY_URL}/${game_id}/empty.json`
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(leaderboardQueryKey(game_id));
        queryClient.invalidateQueries(["monitor"]);
        queryClient.invalidateQueries(["stream"]);
      },
    }
  )

  const set = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <OrganizeContextProvider>
        {!isFetching && !!monitorQueue && (
            <Row>
                <Alert color="success">
                    <AlertWrapper>
                    <Button color="success" onClick={refresh}>
                        {t('Refresh')}
                    </Button>
                    {(() => {
                        switch (monitorQueue) {
                        case 1:
                            return t("This is 1 new submission in the queue.");
                        default:
                            return `${t('There are')} ${monitorQueue} ${t('new submissions in the queue')}.`;
                        }
                    })()}
                    </AlertWrapper>
                </Alert>
            </Row>
        )}
        <AlertDisplay />
        <Row>
            <SidebarLayout
                message={t("If you are moderating your game you will need to approve, reject or feature submissions.")}
                panel={
                    <>
                        <LeaderboardWidget showVisibilityToggle={true} />
                        <Blast />
                    </>
                }
            >
            <>
                <HeaderRow className="gx-1">
                    <Col size="12" lg={decisionFilterType?.enabled ? 'auto' : '6'} className="col--stretch flex-grow-1">
                        <Row className="flex-grow-1 gx-1">
                            <Col size="12" lg={decisionFilterType?.enabled ? '4' : '6'} className="col--stretch">
                                <ChallengeSelect
                                    value={challengeId}
                                    setValue={set(setChallengeId)}
                                />
                            </Col>
                            <Col size="12" lg={decisionFilterType?.enabled ? '4' : '6'} className="col--stretch">
                                <TeamSelect value={teamId} setValue={set(setTeamId)} />
                            </Col>
                            {
                                decisionFilterType?.enabled
                                    ? (
                                        <Col size="12" lg="4" className="col--stretch">
                                            <TypeSelect value={monitorFilterType} setValue={set(setMonitorFilterType)} />
                                        </Col>
                                    )
                                    : null
                            }
                        </Row>
                    </Col>
                    <Col size="12" lg={decisionFilterType?.enabled ? 'auto' : '6'} className="col--stretch">
                        <Row className="flex-grow-1 mt-2 mt-lg-0 gx-1">
                            <Col size="6" sm="4" md="auto" className="col--stretch">
                            {game?.start_anytime === 2 ? (
                                <GameButton className="w-100" color="success" onClick={startGame}>
                                {t('Start Game')} {isStarting && <Spinner size="0.875rem" />}
                                </GameButton>
                            ) : (
                                <ConfirmationModal
                                title={t("Stop Game" )}
                                body={t("Are you sure you would like to stop this game? <br>Once this is done, challenges will be unavailable and hidden from players.")}
                                confirmLabel={t("Yes, stop game")}
                                confirmAction={stopGame}
                                isLoading={isStopping}
                                >
                                <GameButton className="w-100" color="danger">
                                    {t('Stop Game')} {isStopping && <Spinner size="0.875rem" />}
                                </GameButton>
                                </ConfirmationModal>
                            )}
                            </Col>
                            <Col size="6" sm="4" md="auto" className="col--stretch">
                            {game?.user?.super_admin && (
                                <ConfirmationModal 
                                title={t("Delete All" )}
                                body={t("Are you sure you would like to permanently delete all submissions in this game? <br>Once this is done, you will not be able to get them back.")}
                                confirmLabel={t("Yes, delete all")}
                                confirmAction={deleteAll}
                                isLoading={isDeletingAll}
                                >
                                <DeleteButton className="w-100" color="danger">
                                    {t('Delete All')}
                                </DeleteButton>
                                </ConfirmationModal>
                            )}
                            </Col>
                        </Row>
                    </Col>
                </HeaderRow>
                <Row className="mb-2">
                    {pages > 0 && (
                        <Pagination
                            loading={isLoading}
                            page={page}
                            pageSize={pageSize}
                            pages={pages}
                            setPage={setPage}
                            setPageSize={setPageSize}
                        />
                    )}
                </Row>
            </>
            <Loadable 
                loading={isLoading} 
                loader={<AnswerPlaceholder />}
            >
                {
                    answers.length ? (
                        answers.map((answer) => (
                            <React.Fragment key={answer.id}>
                                <Answer
                                {...answer}
                                onFullscreen={setOverlayId}
                                game_id={game_id}
                                regenerate={
                                    (super_admin || administrator || coordinator)
                                }
                                share_location={game.share_location}
                                />
                            </React.Fragment>
                        ))
                    ) : <MonitorEmpty />
                }
                </Loadable>
            </SidebarLayout>
        </Row>
        <Row>
            {pages > 0 && (
                <Pagination
                    loading={isLoading}
                    page={page}
                    pageSize={pageSize}
                    pages={pages}
                    setPage={setPage}
                    setPageSize={setPageSize}
                />
            )}
        </Row>
        {overlayId && (
            <MediaOverlay
            {...answers.find((answer) => answer.id === overlayId)}
            onClick={() => setOverlayId(undefined)}
            />
        )}
        <Refresher 
            listeners={listeners}
            refreshCallbacks={{
                "ss.answer.create": (id) => {
                    setAlert({
                        id: id,
                        type: "info",
                        message: t("The view has been refreshed. It may or may not include the new submission."),
                        closable: true,
                        autoClose: true,
                    })
                },
            }}
        />
    </OrganizeContextProvider>
  );
};

export default withPlaceholder(Monitor, MonitorPlaceholder);
